import { useState, useCallback } from 'react';
import { InventoryRecordsService } from '../api';
import { InventoryRecord, SupplierInfo } from '../types/inventory.types';
import type { InventoryRecordDetailedResponseDto } from '../api';

/**
 * Supplier: prefer record-level field from API; fallback to individualUnits (legacy drafts).
 */
function deriveSupplierFromRecord(
  response: InventoryRecordDetailedResponseDto,
): SupplierInfo | undefined {
  if (response.supplier?.id != null) {
    return {
      id: response.supplier.id,
      name: response.supplier.name ?? '',
    };
  }
  const items = response.items ?? [];
  for (const item of items) {
    const units = item.individualUnits ?? [];
    for (const unit of units) {
      const sid = unit.supplierId;
      if (sid != null && sid > 0) {
        return { id: sid, name: '' };
      }
    }
  }
  return undefined;
}

interface UseInventoryRecordReturn {
  record: InventoryRecord | null;
  loading: boolean;
  error: Error | null;
  fetchRecord: (id: number) => Promise<InventoryRecord | null>;
  resetState: () => void;
}

/**
 * Custom hook for fetching an inventory record with its items
 * Uses the improved backend API that provides consistent data formatting
 */
export function useInventoryRecord(): UseInventoryRecordReturn {
  const [record, setRecord] = useState<InventoryRecord | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const resetState = useCallback(() => {
    setRecord(null);
    setError(null);
    setLoading(false);
  }, []);

  const fetchRecord = useCallback(async (id: number): Promise<InventoryRecord | null> => {
    setLoading(true);
    setError(null);

    try {
      // Use the new detailed endpoint that returns properly formatted data
      // Include warehouseProducts to show individual product details
      const response = await InventoryRecordsService.inventoryRecordsControllerFindOneDetailed(
        id.toString(),
        true, // includeWarehouseProducts = true to show warehouse products in the modal
      );

      if (!response) {
        throw new Error('Failed to fetch inventory record');
      }

      // Derive supplier from items' individualUnits (PENDING drafts store supplierId per unit)
      // Backend does not return supplier at record level; it lives on warehouse products / individualUnits
      const derivedSupplier = deriveSupplierFromRecord(response);

      // The backend now returns properly formatted data, so minimal transformation is needed
      const recordCodeStr = typeof response.recordCode === 'string' ? response.recordCode : '';
      const rawSt = String(response.status || '').toUpperCase();
      const registrationIso = rawSt === 'COMPLETED' ? response.completedAt : null;
      const inventoryRecord: InventoryRecord = {
        id: response.id,
        recordCode: recordCodeStr,
        destinationLocationId: response.destinationLocation?.id ?? 0,
        location: response.destinationLocation?.name ?? 'Sin ubicación',
        date: registrationIso
          ? new Date(registrationIso).toLocaleString('es-ES', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })
          : '—',
        createdAt: response.createdAt,
        completedAt: response.completedAt ?? null,
        status: response.status,
        items: response.items ?? [],
        totalItemsCount: response.totalItemsCount ?? 0,
        notes: undefined,
        warehouseProducts: response.warehouseProducts ?? [],
        supplier: derivedSupplier,
        // Add branch_id for AddInventoryPanel compatibility (expects this when loading draft)
        branch_id: response.destinationLocation?.id,
        destinationLocation: response.destinationLocation
          ? { id: response.destinationLocation.id, name: response.destinationLocation.name }
          : undefined,
      };

      setRecord(inventoryRecord);
      return inventoryRecord;
    } catch (err) {
      console.error('Error fetching inventory record:', err);
      const error = err instanceof Error ? err : new Error('Failed to fetch inventory record');
      setError(error);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { record, loading, error, fetchRecord, resetState };
}
